import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useQueryParams = <T extends string>(args: T[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(() => {
    return args.reduce(
      (acc, curr) => ({ ...acc, [curr]: searchParams.get(curr) }),
      {} as Record<T, string | null>
    );
  }, [searchParams, args]);

  const setQuery = (newParams: Partial<Record<T, string | null>>) => {
    const updatedParams = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === null) {
        updatedParams.delete(key);
      } else {
        updatedParams.set(key, value as string);
      }
    });
    setSearchParams(updatedParams);
  };

  return [query, setQuery] as const;
};

type SerializableValue = string | number | boolean | null | undefined | {} | [];
type QueryParamsState<T> = Record<keyof T, SerializableValue>;
type SetQueryParamsFunction<T> = (
  newParams: Partial<T> | ((prev: T) => Partial<T>)
) => void;

export const useSerializedQueryParams = <T extends QueryParamsState<T>>(
  initialValues: T
): [T, SetQueryParamsFunction<T>, () => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive state from URL params, fallback to initial values
  const urlParams = useMemo((): T => {
    const state = {} as T;

    Object.keys(initialValues).forEach(key => {
      const initialValue = initialValues[key as keyof T];
      const urlValue = searchParams.get(key);

      if (urlValue !== null) {
        state[key as keyof T] = deserializeValue(
          urlValue,
          initialValue
        ) as T[keyof T];
      } else {
        state[key as keyof T] = initialValue;
      }
    });

    return state;
  }, [searchParams, initialValues]);

  // Memoized setter function
  const setUrlParams = useCallback<SetQueryParamsFunction<T>>(
    newParams => {
      const paramsToUpdate =
        typeof newParams === 'function' ? newParams(urlParams) : newParams;

      const newSearchParams = new URLSearchParams(searchParams);

      // Update URL with changed values
      Object.entries(paramsToUpdate).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
        ) {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, serializeValue(value));
        }
      });

      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams, urlParams]
  );

  const clearUrlParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return [urlParams, setUrlParams, clearUrlParams];
};

function serializeValue(value: SerializableValue): string {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function deserializeValue(
  urlValue: string,
  initialValue: SerializableValue
): SerializableValue {
  if (initialValue === null) {
    return urlValue;
  }

  if (typeof initialValue === 'boolean') {
    return urlValue.toLowerCase() === 'true';
  }

  if (typeof initialValue === 'number') {
    const parsed = Number(urlValue);
    return isNaN(parsed) ? initialValue : parsed;
  }

  if (typeof initialValue === 'object') {
    return JSON.parse(urlValue);
  }

  return urlValue;
}
